import React, { useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { MdMic } from "react-icons/md";
import { Tooltip } from "react-tooltip";

import { useToast } from "hooks/toast";
import { useMediaPermissions } from "hooks/useMediaPermissions";

import {
  getDevicesThunk,
  changeMicThunk,
  changeVideoThunk,
  changeAudioThunk,
  persistPreferencesThunk,
  restorePreferencesThunk,
  stopMicLevelUpdate,
} from "store/modules/mediaDevices/actions/thunks";
import { useAppDispatch, useAppSelector } from "store/rootReducer";

import { Drawer } from "components/Drawer";
import { DropdownGlobal } from "components/DropdownGlobal";
import { ContainerMic } from "components/MicLevelDisplay/styles";
import VideoCamera from "components/VideoCamera";
import { videoConstraints } from "components/VideoCamera/constants";

import { Container } from "./styles";

const SelectAudioCamera: React.FC<{
  open: boolean;
  setOpen: (open: boolean) => void;
}> = ({ open, setOpen }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const toast = useToast();
  const permissions = useMediaPermissions();

  const fetchingPreferences = useRef(false);

  const {
    micDevices,
    videoDevices,
    audioDevices,
    selectedMic,
    selectedVideo,
    selectedAudio,
    micLevel,
  } = useAppSelector((state) => state.mediaDevices);

  useEffect(() => {
    if (!open) {
      stopMicLevelUpdate();
    }
  }, [open]);

  useEffect(() => {
    const fetchPreferences = async () => {
      fetchingPreferences.current = true;
      await dispatch(restorePreferencesThunk());
      fetchingPreferences.current = false;
    };

    if (open && !fetchingPreferences.current) {
      fetchPreferences();
    }
  }, [open, dispatch]);

  useEffect(() => {
    const handleDeviceChange = () => {
      dispatch(getDevicesThunk());
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
    };
  }, [dispatch]);

  const onApplyDevices = async () => {
    try {
      await dispatch(persistPreferencesThunk()).unwrap();
      toast.addToast({
        type: "success",
        title: t("devices.configuration"),
        description: t("devices.configurationSaved"),
      });
      setOpen(false);
    } catch {
      //
    }
  };

  return (
    <Drawer
      onClose={() => setOpen(false)}
      title={t("devices.configuration")}
      onApply={permissions.mic ? onApplyDevices : undefined}
      open={open}
      disabled={!permissions.mic}
    >
      <Container>
        {!permissions.mic && <Tooltip id="mic-tooltip" place="top" />}
        <div
          data-tooltip-id={!permissions.mic ? "mic-tooltip" : undefined}
          data-tooltip-content={t("devices.micPermissionRequired")}
        >
          <DropdownGlobal
            wantAnObject
            width="380px"
            defaultValue={selectedMic?.label}
            showDefaultValue={Boolean(selectedMic?.label)}
            options={micDevices}
            labelName={t("labels.microphone")}
            setSelected={(e: any) => {
              dispatch(changeMicThunk(e.id));
            }}
          />
          <div className="container">
            {Array.from({ length: 30 }).map((_, i) => (
              <div
                className="bar"
                key={i}
                style={{
                  backgroundColor:
                    Math.floor(micLevel % 30) > i ? "#A0A0A0" : undefined,
                }}
              />
            ))}
          </div>
          <ContainerMic>
            <MdMic color="blue" />
            <input
              style={{ width: 440 }}
              id="volume"
              min="0"
              max="100"
              value={Number(micLevel)}
              disabled
              type="range"
            />
          </ContainerMic>
        </div>

        {!permissions.audio && <Tooltip id="audio-tooltip" place="top" />}
        <div
          data-tooltip-id={!permissions.audio ? "audio-tooltip" : undefined}
          data-tooltip-content="Permissão de acesso ao áudio necessária"
        >
          <DropdownGlobal
            wantAnObject
            width="380px"
            defaultValue={selectedAudio?.label}
            showDefaultValue={Boolean(selectedAudio?.label)}
            options={audioDevices}
            labelName={t("labels.audio")}
            setSelected={(e: any) => dispatch(changeAudioThunk(e.id))}
          />
        </div>

        {!permissions.camera && <Tooltip id="video-tooltip" place="top" />}
        <div
          data-tooltip-id={!permissions.camera ? "video-tooltip" : undefined}
          data-tooltip-content="Permissão de acesso à câmera necessária"
        >
          <DropdownGlobal
            wantAnObject
            width="380px"
            defaultValue={
              videoDevices.find((d) => d.id === selectedVideo?.id)?.label
            }
            showDefaultValue={Boolean(
              videoDevices.find((d) => d.id === selectedVideo?.id)?.label,
            )}
            options={videoDevices}
            labelName={t("labels.video")}
            setSelected={(e: any) => dispatch(changeVideoThunk(e.id))}
          />
        </div>

        {open && (
          <VideoCamera
            videoConstraints={{
              ...videoConstraints,
              deviceId: selectedVideo?.id
                ? { exact: selectedVideo.id }
                : undefined,
            }}
          />
        )}
      </Container>
    </Drawer>
  );
};

export default SelectAudioCamera;
